/**
 * Feedback Service
 * Handles user feedback submission and retrieval
 */

import { supabase } from '@/services/supabase/supabaseClient';
import { FeedbackInput, Feedback } from '@/types/feedback.types';
import { captureContext } from './contextCapture';

/**
 * Submit user feedback with auto-captured context
 *
 * @param feedback - User-provided feedback data (category, urgency, description)
 * @param screenName - Optional screen name where feedback was triggered
 * @returns Success status and error message if applicable
 */
export const submitFeedback = async (
  feedback: FeedbackInput,
  screenName?: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    console.log('📝 [FeedbackService] Submitting feedback:', feedback.category);

    // Get current user
    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user) {
      console.error('❌ [FeedbackService] User not authenticated');
      return { success: false, error: 'User not authenticated' };
    }

    // Validate input
    if (!feedback.categories || feedback.categories.length === 0) {
      return { success: false, error: 'At least one category is required' };
    }

    // Validate "other" category requires custom text
    if (
      feedback.categories.includes('other') &&
      (!feedback.customCategoryText || feedback.customCategoryText.trim().length === 0)
    ) {
      return { success: false, error: 'Custom category text is required when "Other" is selected' };
    }

    if (feedback.customCategoryText && feedback.customCategoryText.length > 100) {
      return { success: false, error: 'Custom category text must be 100 characters or less' };
    }

    if (!feedback.description || feedback.description.trim().length === 0) {
      return { success: false, error: 'Description is required' };
    }

    if (feedback.description.length > 1000) {
      return { success: false, error: 'Description must be 1000 characters or less' };
    }

    if (feedback.urgency && (feedback.urgency < 1 || feedback.urgency > 5)) {
      return { success: false, error: 'Urgency must be between 1 and 5' };
    }

    // Capture context automatically
    const context = await captureContext(screenName);

    // Get user email from profile
    const { data: profile } = await supabase
      .from('profiles')
      .select('email')
      .eq('id', user.id)
      .single();

    // Insert feedback
    const { error } = await supabase.from('feedback').insert({
      user_id: user.id,
      user_email: profile?.email || user.email,
      category: feedback.categories,
      custom_category_text: feedback.customCategoryText?.trim() || null,
      urgency: feedback.urgency || null,
      description: feedback.description.trim(),
      screen_name: context.screenName,
      app_version: context.appVersion,
      device_info: context.deviceInfo,
      platform: context.platform,
    });

    if (error) {
      console.error('❌ [FeedbackService] Error submitting feedback:', error);
      return { success: false, error: error.message };
    }

    console.log('✅ [FeedbackService] Feedback submitted successfully');
    return { success: true };
  } catch (error) {
    console.error('❌ [FeedbackService] Unexpected error:', error);
    return { success: false, error: 'An unexpected error occurred. Please try again.' };
  }
};

/**
 * Get user's feedback history
 *
 * @returns Array of feedback items or error
 */
export const getUserFeedback = async (): Promise<{
  data: Feedback[] | null;
  error?: string;
}> => {
  try {
    console.log('📋 [FeedbackService] Fetching user feedback history');

    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user) {
      console.error('❌ [FeedbackService] User not authenticated');
      return { data: null, error: 'User not authenticated' };
    }

    const { data, error } = await supabase
      .from('feedback')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('❌ [FeedbackService] Error fetching feedback:', error);
      return { data: null, error: error.message };
    }

    console.log(`✅ [FeedbackService] Fetched ${data?.length || 0} feedback items`);
    return { data };
  } catch (error) {
    console.error('❌ [FeedbackService] Unexpected error:', error);
    return { data: null, error: 'An unexpected error occurred' };
  }
};
